import React, { useCallback } from 'react'
import clsx from 'clsx'
import Draggable, { DraggableEventHandler } from 'react-draggable'

interface Props {
  title: string | React.ReactNode
  resizable?: boolean
  onResize?: (delta: number) => void
  sortable?: boolean
  className?: string
  style?: React.CSSProperties
  id?: string
  order?: 'desc' | 'asc'
  onSortChange?: (order?: 'desc' | 'asc') => void
}

const HeaderCell: React.FC<Props> = ({
  id,
  title,
  className,
  style,
  resizable,
  onResize,
  sortable,
  order,
  onSortChange,
}) => {
  const handleDrag: DraggableEventHandler = useCallback(
    (_, { deltaX }) => {
      if (onResize) {
        onResize(deltaX)
      }
    },
    [onResize],
  )

  const handleSortChange = useCallback(
    (sortOrder: 'desc' | 'asc') => () => {
      if (onSortChange) {
        if (sortOrder === order) {
          onSortChange(undefined)
        } else {
          onSortChange(sortOrder)
        }
      }
    },
    [order, onSortChange],
  )

  return (
    <div
      id={id}
      className={clsx(
        'flex items-center px-2 text-xs font-medium text-gray-600 uppercase border border-gray-500 bg-gray-300 group',
        className,
      )}
      style={style}
      title={title?.toString()}
    >
      <span className="truncate">{title}</span>
      {resizable ? (
        <Draggable
          // @ts-ignore
          position={{ x: 0 }}
          onDrag={handleDrag}
        >
          <button className="absolute top-0 bottom-0 right-0 w-1 bg-gray-500 opacity-0 cursor-resize focus:outline-none group-hover:opacity-100" />
        </Draggable>
      ) : null}
      {sortable ? <div className="flex-1" /> : null}
      {sortable ? (
        <div className="flex flex-col ml-2 buttons-container">
          <button
            className={clsx(
              'focus:outline-none',
              order === 'asc' ? 'text-gray-800' : 'opacity-50',
            )}
            onClick={handleSortChange('asc')}
            title="Sort in Ascending Order"
          >
            <svg
              width="20"
              height="10"
              viewBox="0 0 20 10"
              fill="none"
              className="pointer-events-none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.29279 7.7068C5.48031 7.89427 5.73462 7.99958 5.99979 7.99958C6.26495 7.99958 6.51926 7.89427 6.70679 7.7068L9.99979 4.4138L13.2928 7.7068C13.385 7.80231 13.4954 7.87849 13.6174 7.9309C13.7394 7.98331 13.8706 8.01089 14.0034 8.01205C14.1362 8.0132 14.2678 7.9879 14.3907 7.93762C14.5136 7.88734 14.6253 7.81308 14.7192 7.71919C14.8131 7.6253 14.8873 7.51365 14.9376 7.39075C14.9879 7.26785 15.0132 7.13617 15.012 7.0034C15.0109 6.87062 14.9833 6.7394 14.9309 6.61739C14.8785 6.49539 14.8023 6.38504 14.7068 6.2928L10.7068 2.2928C10.5193 2.10533 10.265 2.00001 9.99979 2.00001C9.73462 2.00001 9.48031 2.10533 9.29279 2.2928L5.29279 6.2928C5.10532 6.48032 5 6.73463 5 6.9998C5 7.26496 5.10532 7.51927 5.29279 7.7068V7.7068Z"
                fill="currentColor"
              />
            </svg>
          </button>
          <button
            className={clsx(
              'focus:outline-none',
              order === 'desc' ? 'text-gray-800' : 'opacity-50',
            )}
            onClick={handleSortChange('desc')}
            title="Sort in Descending Order"
          >
            <svg
              width="20"
              height="10"
              viewBox="0 0 20 10"
              fill="none"
              className="pointer-events-none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.29279 2.30529C5.48031 2.11782 5.73462 2.0125 5.99979 2.0125C6.26495 2.0125 6.51926 2.11782 6.70679 2.30529L9.99979 5.59829L13.2928 2.30529C13.385 2.20978 13.4954 2.1336 13.6174 2.08119C13.7394 2.02878 13.8706 2.00119 14.0034 2.00004C14.1362 1.99888 14.2678 2.02419 14.3907 2.07447C14.5136 2.12475 14.6253 2.199 14.7192 2.29289C14.8131 2.38679 14.8873 2.49844 14.9376 2.62133C14.9879 2.74423 15.0132 2.87591 15.012 3.00869C15.0109 3.14147 14.9833 3.27269 14.9309 3.39469C14.8785 3.5167 14.8023 3.62704 14.7068 3.71929L10.7068 7.71929C10.5193 7.90676 10.265 8.01207 9.99979 8.01207C9.73462 8.01207 9.48031 7.90676 9.29279 7.71929L5.29279 3.71929C5.10532 3.53176 5 3.27745 5 3.01229C5 2.74712 5.10532 2.49282 5.29279 2.30529V2.30529Z"
                fill="currentColor"
              />
            </svg>
          </button>
        </div>
      ) : null}
    </div>
  )
}

export default HeaderCell
